import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton,
  Tooltip, Popover, Divider, ToggleButton, ToggleButtonGroup,
  TextField, InputAdornment,
} from '@mui/material';
import {
  Medication as MedIcon, CheckCircle, Delete, Search,
  AccessTime, History as HistoryIcon, Add, Print,
} from '@mui/icons-material';
import moment from 'moment';
import MedicationAdministrationSheet from './MedicationAdministrationSheet';

// ── Theme tokens ──────────────────────────────────────────────────────
const PRIMARY      = '#1A9B7F';
const PRIMARY_DARK = '#157763';
const PRIMARY_BG   = 'rgba(26,155,127,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';

const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// Prefer the canonical due_at, fall back to legacy `${d_dat} ${time}`.
const dueMoment = (rec) => {
  if (rec?.due_at) {
    const m = moment(rec.due_at);
    if (m.isValid()) return m;
  }
  return moment(`${rec?.d_dat || ''} ${rec?.time || ''}`, 'YYYY-MM-DD hh:mm:ss A');
};

const statusOf = (rec) => {
  const isServed = rec.s_by && rec.s_by !== '-' && rec.s_by !== 'i';
  if (isServed) return 'served';
  if (dueMoment(rec).isBefore(moment())) return 'missed';
  return 'pending';
};

const TONE = {
  served:  { bg: 'rgba(22,163,74,0.10)',  border: '#86EFAC', icon: '✓', color: '#15803D' },
  missed:  { bg: 'rgba(220,38,38,0.10)',  border: '#FCA5A5', icon: '⚠', color: '#991B1B' },
  pending: { bg: 'rgba(217,119,6,0.10)',  border: '#FCD34D', icon: '⏳', color: '#A16207' },
};

/**
 * Inline medication administration sheet for the InPatient panels.
 *
 * Same MAR-grid pattern as MedicationSheetDialog, but actionable: clicking a
 * dose chip opens a small popover with "Mark Served" + "Delete" actions.
 * Defaults to "Today" date filter so the page doesn't scroll forever.
 *
 * Props:
 *   chartedMedications  []
 *   onMarkServed(chart) async
 *   onDelete(chart)     async
 *   onChartNew()        opens the chart-new dialog
 *   chartNewDisabled    boolean
 */
export default function InlineMedicationSheet({
  patient,                              // optional — needed for the Sheet dialog
  chartedMedications = [],
  onMarkServed,
  onDelete,
  onChartNew,
  chartNewDisabled = false,
}) {
  const [search, setSearch]       = useState('');
  const [dateFilter, setDateFilter] = useState('today'); // today | 7d | all
  const [statusFilter, setStatusFilter] = useState('all'); // all | served | pending | missed
  const [popoverEl, setPopoverEl] = useState(null);
  const [popoverChart, setPopoverChart] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Apply filters
  const filtered = useMemo(() => {
    const today = moment().startOf('day');
    return (chartedMedications || []).filter(c => {
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!(c.med || '').toLowerCase().includes(q)) return false;
      }
      const dose = dueMoment(c);
      if (dateFilter === 'today' && !dose.isSame(today, 'day')) return false;
      if (dateFilter === '7d' && dose.isBefore(today.clone().subtract(7, 'days'))) return false;
      if (statusFilter !== 'all' && statusOf(c) !== statusFilter) return false;
      return true;
    });
  }, [chartedMedications, search, dateFilter, statusFilter]);

  // Group by drug
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(c => {
      const fullMed = c.med || '';
      const drug = fullMed.split(' ')[0] || 'Unknown';
      const detail = fullMed.slice(drug.length).trim();
      if (!map.has(drug)) map.set(drug, { drug, detail, doses: [] });
      map.get(drug).doses.push(c);
    });
    const arr = [...map.values()];
    arr.forEach(g => g.doses.sort((a, b) => dueMoment(a).valueOf() - dueMoment(b).valueOf()));
    return arr;
  }, [filtered]);

  // Top-level totals across the *unfiltered* list — these are the "always-true"
  // counts the nurse cares about regardless of current filters.
  const totals = useMemo(() => {
    const all = chartedMedications || [];
    const today = moment().startOf('day');
    const todays = all.filter(c => dueMoment(c).isSame(today, 'day'));
    const meds = new Set(all.map(c => c.medication_trans_code || c.med));
    const served = all.filter(c => statusOf(c) === 'served').length;
    const dueNow = all.filter(c => statusOf(c) === 'pending').length;
    const missed = all.filter(c => statusOf(c) === 'missed').length;
    return { meds: meds.size, served, dueNow, missed, today: todays.length };
  }, [chartedMedications]);

  const handleChipClick = (e, chart) => {
    setPopoverEl(e.currentTarget);
    setPopoverChart(chart);
  };
  const closePopover = () => { setPopoverEl(null); setPopoverChart(null); };

  return (
    <Box>
      {/* Header strip — title + summary + Chart-New button */}
      <Paper elevation={0} sx={{
        p: 2, mb: 1.5, borderRadius: 2,
        border: `1px solid ${BORDER}`, bgcolor: '#fff',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              bgcolor: PRIMARY_BG, color: PRIMARY,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }}>
                Medication Administration
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                {totals.meds} medication{totals.meds === 1 ? '' : 's'} · {totals.today} dose{totals.today === 1 ? '' : 's'} due today
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, gap: 0.75 }}>
            <SummaryPill label="Served"   value={totals.served} color="#15803D" tint="rgba(22,163,74,0.10)" />
            <SummaryPill label="Pending"  value={totals.dueNow} color="#A16207" tint="rgba(217,119,6,0.10)" />
            <SummaryPill label="Missed"   value={totals.missed} color="#991B1B" tint="rgba(220,38,38,0.10)" />
          </Box>

          <Stack direction="row" spacing={0.75}>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => setSheetOpen(true)}
              disabled={(chartedMedications || []).length === 0}
              sx={{
                borderColor: PRIMARY, color: PRIMARY,
                textTransform: 'none', fontWeight: 700,
                '&:hover': { borderColor: PRIMARY_DARK, bgcolor: PRIMARY_BG },
              }}
            >
              Sheet
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onChartNew}
              disabled={chartNewDisabled}
              sx={{
                bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
                textTransform: 'none', fontWeight: 700,
              }}
            >
              Chart Medication
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Filter row */}
      <Paper elevation={0} sx={{
        p: 1.25, mb: 1.5, borderRadius: 2,
        border: `1px solid ${BORDER}`, bgcolor: '#fff',
        display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
      }}>
        <TextField
          size="small" placeholder="Search medication…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small" exclusive
          value={dateFilter}
          onChange={(_, v) => v && setDateFilter(v)}
        >
          {[
            { v: 'today', l: 'Today' },
            { v: '7d',    l: 'Last 7 days' },
            { v: 'all',   l: 'All' },
          ].map(o => (
            <ToggleButton key={o.v} value={o.v} sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
              '&.Mui-selected': { bgcolor: PRIMARY_BG, color: PRIMARY },
            }}>{o.l}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small" exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          {[
            { v: 'all',     l: 'All' },
            { v: 'pending', l: 'Pending' },
            { v: 'served',  l: 'Served' },
            { v: 'missed',  l: 'Missed' },
          ].map(o => (
            <ToggleButton key={o.v} value={o.v} sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
              '&.Mui-selected': { bgcolor: PRIMARY_BG, color: PRIMARY },
            }}>{o.l}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
          {filtered.length} dose{filtered.length === 1 ? '' : 's'} shown
        </Typography>
      </Paper>

      {/* MAR grid */}
      <Paper elevation={0} sx={{
        borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
        overflow: 'hidden',
      }}>
        {grouped.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <MedIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ color: MUTED, fontSize: '0.92rem', fontWeight: 700 }}>
              {chartedMedications.length === 0
                ? 'No medications charted yet'
                : 'No doses match the current filter'}
            </Typography>
            {chartedMedications.length === 0 && (
              <Typography sx={{ color: MUTED, fontSize: '0.78rem', mt: 0.5 }}>
                Click "Chart Medication" to schedule the first dose.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ maxHeight: '52vh', overflowY: 'auto', overflowX: 'hidden' }}>
            {grouped.map((g, gi) => {
              const served = g.doses.filter(d => statusOf(d) === 'served').length;
              const missed = g.doses.filter(d => statusOf(d) === 'missed').length;
              const pending = g.doses.length - served - missed;

              return (
                <Box key={g.drug + gi} sx={{
                  display: 'flex',
                  borderBottom: gi === grouped.length - 1 ? 'none' : `1px solid ${BORDER}`,
                  '&:hover': { bgcolor: '#f8fafc' },
                }}>
                  {/* Sticky drug name column */}
                  <Box sx={{
                    width: 220, flexShrink: 0,
                    p: 1.5, borderRight: `1px solid ${BORDER}`,
                    bgcolor: '#fff', position: 'sticky', left: 0, zIndex: 1,
                  }}>
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }}>
                      {g.drug}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.25 }}>
                      {g.detail || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {served > 0 && <Tag label={`${served} given`} bg="rgba(22,163,74,0.10)" color="#15803D" />}
                      {pending > 0 && <Tag label={`${pending} due`} bg="rgba(217,119,6,0.10)" color="#A16207" />}
                      {missed > 0 && <Tag label={`${missed} missed`} bg="rgba(220,38,38,0.10)" color="#991B1B" />}
                    </Box>
                  </Box>

                  {/* Horizontally scrollable chips */}
                  <Box sx={{
                    flex: 1, minWidth: 0,
                    p: 1, display: 'flex', gap: 0.75,
                    overflowX: 'auto', alignItems: 'center',
                  }}>
                    {g.doses.map((d, di) => {
                      const status = statusOf(d);
                      const tone = TONE[status];
                      const m = dueMoment(d);
                      return (
                        <Tooltip
                          key={d.code || di}
                          title={
                            <Box sx={{ p: 0.5, fontSize: '0.78rem', lineHeight: 1.5 }}>
                              <div><strong>{m.format('ddd D MMM, hh:mm A')}</strong></div>
                              <div>Status: <strong style={{ textTransform: 'capitalize' }}>{status}</strong></div>
                              {status === 'served' && (
                                <>
                                  <div>Given by: {d.s_by}</div>
                                  {d.served_at && moment(d.served_at).isValid() && (
                                    <div>At: {moment(d.served_at).format('hh:mm A')}</div>
                                  )}
                                </>
                              )}
                              <div style={{ opacity: 0.7, marginTop: 4 }}>Click for actions</div>
                            </Box>
                          }
                          arrow placement="top"
                        >
                          <Box
                            role="button"
                            onClick={(e) => handleChipClick(e, d)}
                            sx={{
                              flexShrink: 0, minWidth: 86,
                              px: 1, py: 0.75, borderRadius: 1.5,
                              bgcolor: tone.bg,
                              border: `1px solid ${tone.border}`,
                              display: 'flex', alignItems: 'center', gap: 0.75,
                              cursor: 'pointer',
                              transition: 'transform .12s ease, box-shadow .12s ease',
                              '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 10px rgba(15,23,42,0.06)' },
                            }}
                          >
                            <Typography sx={{ fontSize: '0.85rem', lineHeight: 1 }}>{tone.icon}</Typography>
                            <Box>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: tone.color, lineHeight: 1.1 }}>
                                {m.format('HH:mm')}
                              </Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: tone.color, opacity: 0.85, lineHeight: 1.1 }}>
                                {m.format('D MMM')}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Action popover */}
      <Popover
        open={!!popoverEl}
        anchorEl={popoverEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { borderRadius: 2, p: 1.5, minWidth: 240, border: `1px solid ${BORDER}` } }}
      >
        {popoverChart && (() => {
          const status = statusOf(popoverChart);
          const tone   = TONE[status];
          const m      = dueMoment(popoverChart);
          return (
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: STRONG }}>
                {(popoverChart.med || '').split(' ')[0]}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.25 }}>
                {m.format('ddd D MMM · hh:mm A')}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  px: 1, py: 0.25, borderRadius: 1,
                  bgcolor: tone.bg, color: tone.color,
                  fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>{status}</Box>
                {status === 'served' && popoverChart.s_by && (
                  <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                    by {popoverChart.s_by}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 1.25 }} />

              <Stack direction="row" spacing={1}>
                {status !== 'served' && (
                  <Button
                    fullWidth size="small" variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={async () => {
                      closePopover();
                      if (typeof onMarkServed === 'function') await onMarkServed(popoverChart);
                    }}
                    sx={{
                      bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
                      textTransform: 'none', fontWeight: 700,
                    }}
                  >
                    Mark Served
                  </Button>
                )}
                <Button
                  fullWidth size="small" variant="outlined" color="error"
                  startIcon={<Delete />}
                  onClick={async () => {
                    closePopover();
                    if (typeof onDelete === 'function') await onDelete(popoverChart);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Delete
                </Button>
              </Stack>
            </Box>
          );
        })()}
      </Popover>

      <MedicationAdministrationSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        patient={patient}
      />
    </Box>
  );
}

// ── Local helpers ─────────────────────────────────────────────────────
function SummaryPill({ label, value, color, tint }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      px: 1.25, py: 0.5, borderRadius: 1.5,
      bgcolor: tint, color,
    }}>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}

function Tag({ label, bg, color }) {
  return (
    <Box sx={{
      px: 0.75, py: 0.15, borderRadius: 1,
      bgcolor: bg, color,
      fontSize: '0.62rem', fontWeight: 700,
    }}>
      {label}
    </Box>
  );
}
